// Client-side view of "who am I" for the blog bookmark API, kept as small pure
// functions (storage injected) so the rules are testable.
//
// What the server accepts (BlogEngagementController): the Google session cookie, or
// "Authorization: Bearer <token>" where the token was issued by the backend's
// password-login endpoint. A browser can therefore be in three states:
//   - anonymous:          nothing stored locally                          -> "Sign in to save"
//   - signed in:          a token (or a Google session) the server accepts -> works
//   - signed in LOCALLY but without credentials the server accepts: the user
//     "logged in" (loginuserData exists) against a backend that issues no token,
//     logged in before tokens existed, registered without logging in, or the
//     token expired                                                       -> must sign in again
// The third state used to be reported as the first, which told a logged-in user
// to "sign in" with no explanation.

import { BLOG_API_ORIGIN } from "./blogApi.js";

const BAD_TOKEN_VALUES = new Set(["", "undefined", "null"]);

function browserStorage() {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

// The stored login token, or null. Rejects the strings "undefined"/"null" that
// older code stored when the login response had no token.
export function readAuthToken(storage = browserStorage()) {
  try {
    const token = (storage?.getItem("authToken") || "").trim();
    return BAD_TOKEN_VALUES.has(token) ? null : token;
  } catch {
    return null;
  }
}

// Email of the locally remembered login (loginuserData), or null.
export function readLocalLogin(storage = browserStorage()) {
  try {
    return JSON.parse(storage?.getItem("loginuserData") || "null")?.email || null;
  } catch {
    return null;
  }
}

// Worth asking the server about the saved state? (A Google session leaves the
// "avatar" cookie and/or loginuserData; a password login leaves both token and loginuserData.)
export function looksSignedIn({ storage = browserStorage(), cookie = typeof document === "undefined" ? "" : document.cookie } = {}) {
  return Boolean(readAuthToken(storage) || readLocalLogin(storage) || /(?:^|;\s*)avatar=/.test(cookie));
}

// Authorization header for the bookmark API (never an email).
export function bookmarkAuthHeaders(storage = browserStorage()) {
  const token = readAuthToken(storage);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Why did the server answer 401? "login" = the visitor really is signed out.
// "no-token" / "token-rejected" = the app thinks they are signed in, but the
// server has no credentials it accepts for them - they need to sign in again.
export function unauthorizedKind({ storage = browserStorage(), cookie } = {}) {
  if (!looksSignedIn({ storage, cookie })) return "login";
  return readAuthToken(storage) ? "token-rejected" : "no-token";
}

// Forget the local login (used by "Sign in again"), so the login page starts clean.
export function clearLocalLogin(storage = browserStorage()) {
  for (const key of ["authToken", "loginuserData", "authSource", "pendingOAuth"]) {
    try {
      storage?.removeItem(key);
    } catch {
      // ignore
    }
  }
}

// Exchanges just-entered credentials for a login token without touching the UI.
// For flows that log the user in locally themselves (registration), so they get
// the same credentials the login form would give. Resolves null on any failure.
export async function fetchLoginToken(email, password, { fetchImpl = (...a) => fetch(...a), apiBase = BLOG_API_ORIGIN } = {}) {
  if (!email || !password) return null;
  try {
    const res = await fetchImpl(`${apiBase}/api/v1/user/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.status === true && typeof data.token === "string" && data.token ? data.token : null;
  } catch {
    return null;
  }
}
